import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Banner, Button, Card, Field, Select } from "@magarac/ui";
import {
  NOTIFICATION_LABELS,
  NOTIFICATION_TEMPLATES,
  renderNotification,
  type NotificationTemplate,
} from "@magarac/shared/notifications";
import { localToUtc, todayIn, addDays } from "@magarac/shared/time/tz";
import type { SettingsNotifications } from "@magarac/shared/schemas/settings";
import { HelpIcon } from "../../../components/HelpIcon";
import { messageFor } from "../../../lib/errors";
import { useSetting } from "../../../lib/settings";
import { useTenant } from "../../../lib/tenant";

export const Route = createFileRoute("/_app/settings/notifications")({
  component: NotificationsPage,
});

const WHEN: Record<NotificationTemplate, string> = {
  booking_confirmed: "Right after a visit is booked.",
  visit_reminder: "Before each visit (set how long below).",
  on_the_way: "When your team member taps On my way.",
  visit_rescheduled: "When a visit moves to a new time.",
  visit_canceled: "When a visit is canceled.",
};

/** SCH-23: which texts and emails customers get. Screen key: settings.notifications */
function NotificationsPage() {
  const { active } = useTenant();
  const { query, save, value } = useSetting("notifications");
  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Customer messages</h1>
        <HelpIcon screen="settings.notifications" />
      </div>
      {query.isPending ? (
        <p className="text-muted">Loading…</p>
      ) : (
        <NotificationsForm
          key={active?.id ?? "none"}
          initial={value ?? { disabled: [], reminderHoursBefore: 24 }}
          onSave={(v) => save.mutateAsync(v)}
          busy={save.isPending}
          error={save.error ? messageFor(save.error) : null}
          saved={save.isSuccess}
        />
      )}
    </div>
  );
}

function NotificationsForm({
  initial,
  onSave,
  busy,
  error,
  saved,
}: {
  initial: SettingsNotifications;
  onSave: (v: SettingsNotifications) => Promise<unknown>;
  busy: boolean;
  error: string | null;
  saved: boolean;
}) {
  const { active } = useTenant();
  const business = useSetting("business").value;
  const scheduling = useSetting("scheduling").value;
  const [form, setForm] = useState<SettingsNotifications>(initial);
  const [preview, setPreview] = useState<NotificationTemplate | null>(null);
  const tz = scheduling?.timezone ?? "America/Chicago";
  const sampleDay = addDays(todayIn(tz), 2);
  const sample = (t: NotificationTemplate) =>
    renderNotification(t, {
      businessName: business?.name ?? active?.name ?? "Your business",
      businessPhone: business?.phone ?? null,
      customerFirstName: "Pat",
      serviceName: "your service",
      windowStart: new Date(localToUtc(sampleDay, 8 * 60, tz)).toISOString(),
      windowEnd: new Date(localToUtc(sampleDay, 10 * 60, tz)).toISOString(),
      timezone: tz,
      addressLine: "12 Elm St, Austin",
      techFirstName: "Marcus",
    });
  const toggle = (t: NotificationTemplate) =>
    setForm({
      ...form,
      disabled: form.disabled.includes(t)
        ? form.disabled.filter((x) => x !== t)
        : [...form.disabled, t],
    });

  return (
    <>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {saved ? <Banner tone="ok">Saved.</Banner> : null}
      <Card>
        <p className="mb-4 text-muted">
          Customers get a text when they have a mobile number, or an email when they do not. Texts
          are signed with your business name. Nothing routine is sent between 9 PM and 8 AM; it
          waits until morning. Customers who reply STOP get emails only.
        </p>
        <ul className="divide-y divide-line">
          {NOTIFICATION_TEMPLATES.map((t) => {
            const on = !form.disabled.includes(t);
            return (
              <li key={t} className="py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      className="h-5 w-5"
                      checked={on}
                      onChange={() => toggle(t)}
                    />
                    <span>
                      <span className="block font-medium">{NOTIFICATION_LABELS[t]}</span>
                      <span className="block text-sm text-muted">{WHEN[t]}</span>
                    </span>
                  </label>
                  <Button
                    variant="ghost"
                    onClick={() => setPreview(preview === t ? null : t)}
                    aria-expanded={preview === t}
                  >
                    {preview === t ? "Hide example" : "See example"}
                  </Button>
                </div>
                {preview === t ? (
                  <div className="mt-2 rounded-lg bg-canvas p-3 text-sm">
                    <p className="mb-1 font-semibold">Text</p>
                    <p className="mb-3">{sample(t).sms}</p>
                    <p className="mb-1 font-semibold">Email: {sample(t).emailSubject}</p>
                    <p className="whitespace-pre-wrap">{sample(t).emailText}</p>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
        <Field id="reminder-hours" label="Send the reminder">
          <Select
            id="reminder-hours"
            value={String(form.reminderHoursBefore)}
            disabled={form.disabled.includes("visit_reminder")}
            onChange={(e) => setForm({ ...form, reminderHoursBefore: Number(e.target.value) })}
          >
            {[2, 4, 12, 24, 48, 72].map((h) => (
              <option key={h} value={h}>
                {h < 24
                  ? `${h} hours before`
                  : h === 24
                    ? "The day before (24 hours)"
                    : `${h / 24} days before`}
              </option>
            ))}
          </Select>
        </Field>
        {!business?.phone ? (
          <Banner tone="warn">
            Add your business phone under Business details so messages can tell customers how to
            reach you.
          </Banner>
        ) : null}
        <Button busy={busy} onClick={() => void onSave(form)}>
          Save
        </Button>
      </Card>
    </>
  );
}
